import { useState } from "react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { supabase } from "~/integrations/supabase/client";

interface TestResult {
  testName: string;
  type: number;
  success: boolean;
  response?: any;
  error?: string;
  httpStatus?: number;
}

interface TestResponse {
  success: boolean;
  test_mode: string;
  document_id: number;
  total_tests: number;
  results: TestResult[];
  analysis: {
    summary: {
      total: number;
      successful: number;
      failed: number;
      success_rate: string;
    };
    by_type: Record<
      number,
      { success: boolean; count: number; responses: any[] }
    >;
    successful_types: number[];
    failed_types: number[];
  };
  recommendations: string[];
}

export default function TestCreditNotesPage() {
  const [documentId, setDocumentId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<TestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const testCreditNotes = async (testMode: "safe" | "full") => {
    if (!documentId) {
      setError("Please enter a document ID");
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      // Mock document details for testing
      const mockDocumentDetails = [
        {
          id: parseInt(documentId) + 1000, // Mock detail ID
          quantity: 5,
          netAmount: 100.0,
          description: "Test Item 1",
        },
        {
          id: parseInt(documentId) + 1001, // Mock detail ID
          quantity: 2,
          netAmount: 75.5,
          description: "Test Item 2",
        },
      ];

      const { data, error } = await supabase.functions.invoke(
        "test-bsale-credit-types",
        {
          body: {
            document_id: parseInt(documentId),
            document_details: mockDocumentDetails,
            test_mode: testMode,
          },
        }
      );

      if (error) {
        throw new Error(error.message);
      }

      setResults(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mx-auto max-w-6xl">
        <h1 className="mb-8 text-3xl font-bold">
          BSale Credit Note API Testing
        </h1>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Test Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="documentId">BSale Document ID</Label>
              <Input
                id="documentId"
                type="number"
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
                placeholder="Enter a valid BSale document ID (e.g., 123)"
              />
              <p className="mt-1 text-sm text-muted-foreground">
                Enter the ID of an existing BSale document to test credit note
                creation
              </p>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={() => testCreditNotes("safe")}
                disabled={isLoading}
                variant="outline"
              >
                {isLoading ? "Testing..." : "Safe Test (No API Calls)"}
              </Button>
              <Button
                onClick={() => testCreditNotes("full")}
                disabled={isLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {isLoading ? "Testing..." : "⚠️ Full Test (Real API Calls)"}
              </Button>
            </div>

            <div className="text-sm text-muted-foreground">
              <p>
                <strong>Safe Mode:</strong> No actual API calls, only validates
                payload structure
              </p>
              <p>
                <strong>Full Mode:</strong> Makes real BSale API calls (use
                carefully!)
              </p>
            </div>
          </CardContent>
        </Card>

        {error && (
          <Card className="mb-8 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-800">{error}</p>
            </CardContent>
          </Card>
        )}

        {results && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Test Results
                <Badge
                  variant={
                    results.analysis.summary.successful > 0
                      ? "default"
                      : "destructive"
                  }
                >
                  {results.analysis.summary.success_rate} Success Rate
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="summary" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                  <TabsTrigger value="results">Test Results</TabsTrigger>
                  <TabsTrigger value="analysis">Analysis</TabsTrigger>
                  <TabsTrigger value="recommendations">
                    Recommendations
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="summary" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-2xl font-bold">
                          {results.analysis.summary.total}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Total Tests
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-2xl font-bold text-green-600">
                          {results.analysis.summary.successful}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Successful
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-2xl font-bold text-red-600">
                          {results.analysis.summary.failed}
                        </div>
                        <p className="text-xs text-muted-foreground">Failed</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-2xl font-bold">
                          {results.analysis.summary.success_rate}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Success Rate
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <div>
                    <h3 className="mb-2 text-lg font-semibold">
                      Successful Types
                    </h3>
                    <div className="flex gap-2">
                      {results.analysis.successful_types.map((type) => (
                        <Badge key={type} variant="default">
                          Type {type}
                        </Badge>
                      ))}
                      {results.analysis.successful_types.length === 0 && (
                        <p className="text-muted-foreground">
                          No successful types
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-2 text-lg font-semibold">Failed Types</h3>
                    <div className="flex gap-2">
                      {results.analysis.failed_types.map((type) => (
                        <Badge key={type} variant="destructive">
                          Type {type}
                        </Badge>
                      ))}
                      {results.analysis.failed_types.length === 0 && (
                        <p className="text-muted-foreground">No failed types</p>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="results" className="space-y-4">
                  {results.results.map((result, index) => (
                    <Card
                      key={index}
                      className={
                        result.success ? "border-green-200" : "border-red-200"
                      }
                    >
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          {result.testName}
                          <div className="flex gap-2">
                            <Badge
                              variant={
                                result.success ? "default" : "destructive"
                              }
                            >
                              {result.success ? "Success" : "Failed"}
                            </Badge>
                            <Badge variant="outline">Type {result.type}</Badge>
                            {result.httpStatus && (
                              <Badge variant="outline">
                                HTTP {result.httpStatus}
                              </Badge>
                            )}
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {result.error && (
                          <p className="mb-2 text-red-600">{result.error}</p>
                        )}
                        {result.response && (
                          <pre className="overflow-x-auto rounded bg-gray-100 p-4 text-sm">
                            {JSON.stringify(result.response, null, 2)}
                          </pre>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="analysis" className="space-y-4">
                  <div>
                    <h3 className="mb-4 text-lg font-semibold">
                      Type Analysis
                    </h3>
                    {Object.entries(results.analysis.by_type).map(
                      ([type, data]) => (
                        <Card key={type} className="mb-4">
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              Type {type}
                              <Badge
                                variant={
                                  data.success ? "default" : "destructive"
                                }
                              >
                                {data.success ? "Working" : "Failed"}
                              </Badge>
                              <Badge variant="outline">
                                {data.count} tests
                              </Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {data.responses.length > 0 && (
                              <pre className="overflow-x-auto rounded bg-gray-100 p-4 text-sm">
                                {JSON.stringify(data.responses[0], null, 2)}
                              </pre>
                            )}
                          </CardContent>
                        </Card>
                      )
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="recommendations" className="space-y-4">
                  <div>
                    <h3 className="mb-4 text-lg font-semibold">
                      Implementation Recommendations
                    </h3>
                    <div className="space-y-2">
                      {results.recommendations.map((rec, index) => (
                        <Card key={index}>
                          <CardContent className="pt-6">
                            <p>{rec}</p>
                          </CardContent>
                        </Card>
                      ))}
                      {results.recommendations.length === 0 && (
                        <p className="text-muted-foreground">
                          No specific recommendations available.
                        </p>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
